<script>
export default {
  name: 'CompaniesSnsOauth',
}
</script>
<script setup>
import { computed, reactive } from 'vue'
import { useStore } from 'vuex'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import { KkLoading, KkIcon, KkButton } from '../../../components/base'
import { KkInfo } from '../../../components/ui'
import { KkTutorial } from '../../../components/projects'
import { KkOauthAccountsModal, KkOauthInstagramConnectModal } from '../../../components/domains/sns'
import snsOauthImagePath from '../../../../../images/sns/oauth.png'
import iconFacebookPath from '../../../../../images/sns/icon_facebook.svg'
import iconInstagramPath from '../../../../../images/sns/icon_instagram.svg'
import iconTwitterPath from '../../../../../images/sns/icon_x.svg'
const store = useStore()
const { t } = useI18n()
const { company_uuid } = useRoute().params
const facebookPages = computed(() => store.state.companiesSnsFacebookPages.data)
const facebookPagesLoading = computed(() => store.state.companiesSnsFacebookPages.loading)
const instagramBusinessAccounts = computed(() => store.state.companiesSnsInstagramBusinessAccounts.data)
const instagramBusinessAccountsLoading = computed(() => store.state.companiesSnsInstagramBusinessAccounts.loading)
const twitterUserProfiles = computed(() => store.state.companiesSnsTwitterUserProfiles.data)
const twitterUserProfilesLoading = computed(() => store.state.companiesSnsTwitterUserProfiles.loading)
const companyApprovalFlow = computed(() => store.state.companiesPostsManagementsApprovalFlows.data)
const showOnlyOwnerAuthority = computed(() => store.getters['companiesAccessibleApi/showOnlyOwnerAuthority'])
const readFacebookPages = () => store.dispatch('companiesSnsFacebookPages/readFacebookPages', true)
const deleteFacebookPages = (ids) => store.dispatch('companiesSnsFacebookPages/deleteFacebookPages', { ids })
const readInstagramBusinessAccounts = () => store.dispatch('companiesSnsInstagramBusinessAccounts/readInstagramBusinessAccounts')
const createInstagramBusinessAccounts = (appPageIds) => store.dispatch('companiesSnsInstagramBusinessAccounts/createInstagramBusinessAccounts', { app_page_ids: appPageIds })
const deleteInstagramBusinessAccounts = (ids) => store.dispatch('companiesSnsInstagramBusinessAccounts/deleteInstagramBusinessAccounts', { ids })
const readTwitterUserProfiles = () => store.dispatch('companiesSnsTwitterUserProfiles/readTwitterUserProfiles')
const deleteTwitterUserProfiles = (ids) => store.dispatch('companiesSnsTwitterUserProfiles/deleteTwitterUserProfiles', { ids })
const readApprovalFlow = () => store.dispatch('companiesPostsManagementsApprovalFlows/readApprovalFlow')
const loading = computed(() => facebookPagesLoading.value || instagramBusinessAccountsLoading.value || twitterUserProfilesLoading.value)
const showHashtagMenu = computed(() => {
  // 承認機能がオフ && Instagramアカウントが連携済み の場合のみ「ハッシュタグ連携設定」メニューを表示する
  const isAvailableApprovalFlow = companyApprovalFlow.value && companyApprovalFlow.value.postApprovalFlow
  return !isAvailableApprovalFlow && instagramBusinessAccounts.value.length > 0
})
const state = reactive({
  openModalType: null,
  openInstagramConnectModal: false,
  showTutorial: true
})
const handleDisconnectAccount = async (ids) => {
  // instagramのアカウント連携数が1→0になる時にはハッシュタグ連携設定も解除されるアラートを出す
  const showHashtagAlert = showHashtagMenu.value && state.openModalType === 'instagram' && ids.length === instagramBusinessAccounts.value.length
  if(!confirm(t(`pages.companies.snsOauth.${showHashtagAlert ? 'deleteConfirmWithHashtagAlert' : 'deleteConfirm'}`))) return
  switch (state.openModalType) {
  case 'facebook':
    await deleteFacebookPages(ids)
    readFacebookPages()
    readInstagramBusinessAccounts()
    break
  case 'instagram':
    await deleteInstagramBusinessAccounts(ids)
    readFacebookPages()
    readInstagramBusinessAccounts()
    break
  case 'twitter':
    await deleteTwitterUserProfiles(ids)
    readTwitterUserProfiles()
    break
  }
  state.openModalType = null
}
const handleSubmitConnectInstagram = async (appPageIds) => {
  state.openInstagramConnectModal = false
  await createInstagramBusinessAccounts(appPageIds)
  readInstagramBusinessAccounts()
}
const handleInstagramConnect = () => {
  if(facebookPages.value.length !== 0) {
    state.openInstagramConnectModal = true
  } else if (confirm(t('pages.companies.snsOauth.mustConnectFacebook'))) {
    window.location.href =  `/facebook_oauth/authorization/new?company_uuid=${ company_uuid }`
  }
}
const modalAccounts = () => {
  switch (state.openModalType) {
  case 'facebook':
    return facebookPages.value
  case 'instagram':
    return instagramBusinessAccounts.value
  case 'twitter':
    return twitterUserProfiles.value
  }
}
const load = async () => {
  await Promise.all([
    readFacebookPages(),
    readInstagramBusinessAccounts(),
    readTwitterUserProfiles(),
    readApprovalFlow(),
  ])
}
load()
</script>
<template>
  <div>
    <KkLoading v-if="loading" />
    <KkTutorial
      v-else-if="facebookPages.length === 0 && twitterUserProfiles.length === 0 && state.showTutorial"
      :description1="t('pages.companies.snsOauth.noConnectedAccount.notice')"
      :img-path="snsOauthImagePath"
      :tutorial-description="t('pages.companies.snsOauth.noConnectedAccount.description1')"
      :available-service="t('pages.companies.snsOauth.noConnectedAccount.description2')"
    >
      <template v-if="showOnlyOwnerAuthority" #buttonArea>
        <KkButton
          color="primary"
          :text="$t('labels.connectWith', { value: $t('labels.snsAccount') })"
          @click="state.showTutorial = false"
        />
      </template>
    </KkTutorial>
    <div v-else class="p-box">
      <div class="p-box__body">
        <KkInfo icon="hint" class="snsInfo">
          {{ $t('pages.companies.snsOauth.info') }}
        </KkInfo>
        <div class="p-table__content">
          <table>
            <thead>
              <tr>
                <th>
                  {{ $t('pages.companies.snsOauth.header.sns') }}
                </th>
                <th>
                  {{ $t('pages.companies.snsOauth.header.account') }}
                </th>
                <th />
              </tr>
              <tr class="p-table__content__space"><td colspan="5" /></tr>
            </thead>
            <tbody>
              <tr class="snsContainer">
                <td class="snsContainer__title">
                  <span class="snsContainer__icon">
                    <img alt="" :src="iconFacebookPath">
                  </span>
                  {{ $t('pages.companies.snsOauth.facebook') }}
                </td>
                <td>
                  <template v-if="facebookPages.length === 0">
                    {{ $t('pages.companies.snsOauth.notConnect') }}
                  </template>
                  <div v-else>
                    <span
                      class="snsContainer__modalLink"
                      @click="state.openModalType = 'facebook'"
                    >
                      {{ $t('pages.companies.snsOauth.accountCount', { count: facebookPages.length }) }}
                    </span>
                  </div>
                </td>
                <td class="snsContainer__action">
                  <a :href="`/facebook_oauth/authorization/new?company_uuid=${ company_uuid }`" class="action">
                    <KkIcon name="iconPlus" color="rgba(58, 59, 63, 0.7)" class="action__icon" />
                    {{ $t('pages.companies.snsOauth.add') }}
                  </a>
                </td>
              </tr>
              <tr class="snsContainer">
                <td class="snsContainer__title">
                  <span class="snsContainer__icon">
                    <img alt="" :src="iconInstagramPath">
                  </span>
                  {{ $t('pages.companies.snsOauth.instagram') }}
                </td>
                <td>
                  <template v-if="instagramBusinessAccounts.length === 0">
                    {{ $t('pages.companies.snsOauth.notConnect') }}
                  </template>
                  <div v-else>
                    <span
                      class="snsContainer__modalLink"
                      @click="state.openModalType = 'instagram'"
                    >
                      {{ $t('pages.companies.snsOauth.accountCount', { count: instagramBusinessAccounts.length }) }}
                    </span>
                  </div>
                </td>
                <td class="snsContainer__action">
                  <div class="action" @click="handleInstagramConnect">
                    <KkIcon name="iconPlus" color="rgba(58, 59, 63, 0.7)" class="action__icon" />
                    {{ $t('pages.companies.snsOauth.add') }}
                  </div>
                  <router-link v-if="showHashtagMenu" class="action" :to="{ name: 'CompaniesSnsInstagramHashtagList'}">
                    <KkIcon name="detail" color="rgba(58, 59, 63, 0.7)" class="action__icon" />
                    {{ $t('pages.companies.snsOauth.connectHashtag') }}
                  </router-link>
                </td>
              </tr>
              <tr class="snsContainer">
                <td class="snsContainer__title">
                  <span class="snsContainer__icon">
                    <img alt="" :src="iconTwitterPath">
                  </span>
                  {{ $t('pages.companies.snsOauth.twitter') }}
                </td>
                <td>
                  <template v-if="twitterUserProfiles.length === 0">
                    {{ $t('pages.companies.snsOauth.notConnect') }}
                  </template>
                  <div v-else>
                    <span
                      class="snsContainer__modalLink"
                      @click="state.openModalType = 'twitter'"
                    >
                      {{ $t('pages.companies.snsOauth.accountCount', { count: twitterUserProfiles.length }) }}
                    </span>
                  </div>
                </td>
                <td class="snsContainer__action">
                  <a :href="`/twitter_oauth/authorization/new?company_uuid=${ company_uuid }`" class="action">
                    <KkIcon name="iconPlus" color="rgba(58, 59, 63, 0.7)" class="action__icon" />
                    {{ $t('pages.companies.snsOauth.add') }}
                  </a>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <KkOauthAccountsModal
        v-if="!!state.openModalType"
        :accounts="modalAccounts()"
        :type="state.openModalType"
        :open-modal="!!state.openModalType"
        @close="state.openModalType = null"
        @submit="handleDisconnectAccount"
      />
      <KkOauthInstagramConnectModal
        v-if="state.openInstagramConnectModal"
        :accounts="facebookPages.filter(v => v.hasConnectableInstagram)"
        :open-modal="state.openInstagramConnectModal"
        @close="state.openInstagramConnectModal = null"
        @submit="handleSubmitConnectInstagram"
      />
    </div>
  </div>
</template>
<style lang="scss" scoped>
@import "../../../../../stylesheets/biz_spa/foundation/var";
.noConnectedAccount {
  &__topSection {
    display: flex;
    margin-bottom: 24px;
  }
  &__image {
    max-width: 320px;
  }
  &__description {
    margin: auto auto auto 40px;
    font-size: 16px;
    white-space: pre-wrap;
  }
  &__notice {
    white-space: pre-wrap;
  }
  &__serviceList {
    margin-top: 15px;
    font-weight: bold;
    white-space: pre-wrap;
  }
}
.snsContainer {
  &__title {
    display: flex;
    align-items: center;
    padding: 24px 0 24px 16px;
    border-top-left-radius: 4px;
    border-bottom-left-radius: 4px;
  }
  &__icon {
    display: flex;
    img {
      width: 32px;
      height: 32px;
      margin-right: 10px;
      border-radius: 50%;
    }
  }
  &__action {
    border-top-right-radius: 4px;
    border-bottom-right-radius: 4px;
  }
  &__modalLink {
    color: $color-accent-primary;
    text-decoration: underline;
    cursor: pointer;
  }
}
.p-table__content {
  table {
    border-spacing: 0 8px;
  }
  tbody {
    tr {
      border-radius: 4px;
    }
    td {
      vertical-align: middle;
      border-bottom: 1px solid #E4E7EB;
    }
    td:not(:first-child) {
      border-left: none;
    }
  }
}
.action {
  display: flex;
  text-decoration: none;
  cursor: pointer;
  &__icon {
    width: 16px;
    margin-right: 5px;
  }
}
.snsInfo {
  margin-bottom: 10px;
}
</style>
